/**
 * Popup sequence helpers:
 * - Mobile: Orbe geolocation → cookies → Klaviyo 10% (one form).
 * - Desktop: hold 10% until cookies (and Orbe if it is open).
 * - Shield cookie clicks so they don't dismiss Klaviyo.
 * - Auto marketing overlays stay held until first real user click (Notify Me etc. stay usable).
 */
(function () {
  var HOLD_KLAVIYO = 'koops-awaiting-consent';
  var HOLD_COOKIES = 'koops-hold-cookies';
  var COOKIE_VISIBLE = 'koops-cookie-banner-visible';
  var ALLOW_OVERLAYS = 'koops-allow-overlays';
  var COOKIE_UI =
    '#shopify-pc__banner, #shopify-pc__prefs, [id^="shopify-pc__"], .shopify-pc__banner__dialog, .shopify-pc__prefs';
  var ORBE_ROOT = '#md-app-embed__modal';
  var SHIELD_MS = 700;
  var POLL_MS = 200;
  var CONSENT_MAX_MS = 5000;
  var ORBE_APPEAR_MS = 2500;
  var ORBE_MAX_MS = 30000;

  var shielded = [];
  var clearTimer;
  var klaviyoReleased = false;
  var consentReady = false;
  var orbePhaseDone = false;
  var orbeSeen = false;
  var orbeGraceUntil = 0;
  var bannerObserver;
  var orbeObserver;
  var started = Date.now();

  function root() {
    return document.documentElement;
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  function customerPrivacy() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function consentResolved() {
    var cp = customerPrivacy();
    if (!cp || typeof cp.shouldShowBanner !== 'function') return false;
    return !cp.shouldShowBanner();
  }

  function isOrbeModalVisible() {
    var modal = document.querySelector(ORBE_ROOT);
    if (!modal || modal.classList.contains('orbe--hidden')) return false;

    var style = window.getComputedStyle(modal);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    var content = modal.querySelector('.md-modal__content');
    if (!content) return false;

    var rect = content.getBoundingClientRect();
    var visible = rect.width > 2 && rect.height > 2;
    if (visible) orbeSeen = true;
    return visible;
  }

  function holdKlaviyo() {
    if (klaviyoReleased) return;
    root().classList.add(HOLD_KLAVIYO);
  }

  function holdCookiesForOrbe() {
    if (!isMobile()) {
      root().classList.remove(HOLD_COOKIES);
      return;
    }
    if (orbePhaseDone) {
      root().classList.remove(HOLD_COOKIES);
      return;
    }
    root().classList.add(HOLD_COOKIES);
  }

  function revealCookiesAfterOrbe() {
    orbePhaseDone = true;
    root().classList.remove(HOLD_COOKIES);
    syncCookieStackHeight();
  }

  function releaseKlaviyo() {
    if (klaviyoReleased) return;
    klaviyoReleased = true;
    root().classList.remove(HOLD_KLAVIYO);
    root().classList.remove(ALLOW_OVERLAYS);
    revealCookiesAfterOrbe();
    syncCookieStackHeight();
    // Do not call openForm — that bypasses Klaviyo's "don't show again" and
    // reopens the 10% popup on every navigation. Releasing the CSS hold is enough.
  }

  function advanceOrbePhase() {
    if (orbePhaseDone) return;

    var orbeOpen = isOrbeModalVisible();

    // Still waiting for Orbe to appear on mobile.
    if (isMobile() && !orbeSeen && Date.now() < orbeGraceUntil) {
      holdCookiesForOrbe();
      return;
    }

    // Orbe is showing — keep cookies hidden on mobile.
    if (orbeOpen) {
      holdCookiesForOrbe();
      return;
    }

    // Orbe closed, never appeared, or desktop — cookies can show.
    revealCookiesAfterOrbe();
    tryReleaseKlaviyo();
  }

  function tryReleaseKlaviyo() {
    if (klaviyoReleased) return;

    // On mobile: geo first, then cookies, then 10%.
    if (isMobile() && !orbePhaseDone) return;

    if (!(consentReady || consentResolved())) return;

    consentReady = true;

    // If Orbe somehow still open (desktop overlap), wait.
    if (isOrbeModalVisible()) return;

    releaseKlaviyo();
  }

  function markConsentReady() {
    consentReady = true;
    tryReleaseKlaviyo();
  }

  function syncHoldFromApi() {
    if (consentResolved()) {
      markConsentReady();
      return true;
    }
    holdKlaviyo();
    return false;
  }

  function isCookieUi(node) {
    return Boolean(node && node.closest && node.closest(COOKIE_UI));
  }

  function isOrbeUi(node) {
    return Boolean(node && node.closest && node.closest(ORBE_ROOT));
  }

  function allowOverlaysForUser() {
    if (klaviyoReleased) return;
    root().classList.add(ALLOW_OVERLAYS);
  }

  function clearShield() {
    shielded.forEach(function (el) {
      if (!el || !el.hasAttribute('data-koops-pe')) return;
      el.style.pointerEvents = el.getAttribute('data-koops-pe');
      el.removeAttribute('data-koops-pe');
    });
    shielded = [];
  }

  function shieldOverlays() {
    clearShield();
    document.querySelectorAll('div.needsclick, .klaviyo-form, [class*="klaviyo"]').forEach(function (el) {
      if (!el || el.closest(COOKIE_UI)) return;
      var style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return;
      var role = el.getAttribute('role');
      var isOverlay =
        role === 'dialog' ||
        style.position === 'fixed' ||
        el.classList.contains('klaviyo-form') ||
        (el.classList.contains('needsclick') && el.getBoundingClientRect().width > window.innerWidth * 0.5);

      if (!isOverlay) return;
      if (!el.hasAttribute('data-koops-pe')) {
        el.setAttribute('data-koops-pe', el.style.pointerEvents || '');
      }
      el.style.pointerEvents = 'none';
      shielded.push(el);
    });
  }

  function armShield() {
    shieldOverlays();
    if (clearTimer) clearTimeout(clearTimer);
    clearTimer = setTimeout(clearShield, SHIELD_MS);
  }

  function onCookieDecisionClick(event) {
    var target = event.target;
    if (!isCookieUi(target)) return;

    armShield();

    if (
      target.closest &&
      target.closest(
        '#shopify-pc__banner__btn-accept, #shopify-pc__banner__btn-decline, #shopify-pc__prefs__header-accept, #shopify-pc__prefs__header-decline, #shopify-pc__prefs__btn-save, [id*="shopify-pc__"][id*="accept"], [id*="shopify-pc__"][id*="decline"]'
      )
    ) {
      setTimeout(markConsentReady, 50);
      setTimeout(function () {
        if (consentResolved()) markConsentReady();
      }, 300);
    }

    event.stopPropagation();
  }

  function syncCookieStackHeight() {
    var banner = document.getElementById('shopify-pc__banner');
    var rootEl = root();
    if (!banner) {
      rootEl.classList.remove(COOKIE_VISIBLE);
      rootEl.style.removeProperty('--koops-cookie-stack-height');
      return;
    }

    var style = window.getComputedStyle(banner);
    var visible =
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      banner.getBoundingClientRect().height > 0 &&
      !rootEl.classList.contains(HOLD_COOKIES);

    rootEl.classList.toggle(COOKIE_VISIBLE, visible);

    if (!visible) {
      rootEl.style.removeProperty('--koops-cookie-stack-height');
      return;
    }

    var heightPx = Math.ceil(banner.getBoundingClientRect().height);
    rootEl.style.setProperty('--koops-cookie-stack-height', heightPx / 10 + 'rem');
  }

  function watchBanner() {
    var banner = document.getElementById('shopify-pc__banner');
    syncCookieStackHeight();
    if (!banner || bannerObserver) return;

    bannerObserver = new MutationObserver(function () {
      var el = document.getElementById('shopify-pc__banner');
      syncCookieStackHeight();
      if (!el) {
        markConsentReady();
        return;
      }
      if (window.getComputedStyle(el).display === 'none' && consentResolved()) {
        markConsentReady();
      }
    });
    bannerObserver.observe(banner, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
  }

  function watchOrbe() {
    var modal = document.querySelector(ORBE_ROOT);
    advanceOrbePhase();
    tryReleaseKlaviyo();

    if (!modal || orbeObserver) return;
    orbeObserver = new MutationObserver(function () {
      advanceOrbePhase();
      tryReleaseKlaviyo();
    });
    orbeObserver.observe(modal, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
      childList: true,
      subtree: true,
    });
  }

  // Boot: hold 10%. On mobile, hide cookies until Orbe finishes.
  holdKlaviyo();
  if (isMobile()) {
    orbeGraceUntil = Date.now() + ORBE_APPEAR_MS;
    holdCookiesForOrbe();
  } else {
    orbePhaseDone = true;
  }

  document.addEventListener('visitorConsentCollected', markConsentReady);

  document.addEventListener(
    'pointerdown',
    function (event) {
      if (isCookieUi(event.target)) {
        armShield();
        return;
      }
      if (isOrbeUi(event.target)) return;
      // Any real page interaction (e.g. Notify Me) must not be blocked by the marketing hold.
      allowOverlaysForUser();
    },
    true
  );

  document.addEventListener('click', onCookieDecisionClick, false);

  window.addEventListener('resize', function () {
    syncCookieStackHeight();
    if (isMobile() && !orbePhaseDone) holdCookiesForOrbe();
    else revealCookiesAfterOrbe();
    advanceOrbePhase();
    tryReleaseKlaviyo();
  });

  var poll = setInterval(function () {
    watchBanner();
    watchOrbe();
    syncHoldFromApi();
    advanceOrbePhase();

    var tooLongConsent = Date.now() - started > CONSENT_MAX_MS;
    var tooLongOrbe = Date.now() - started > ORBE_MAX_MS;

    if (tooLongOrbe && !orbePhaseDone) revealCookiesAfterOrbe();
    if (tooLongConsent && !customerPrivacy()) markConsentReady();
    if (tooLongConsent && consentResolved()) markConsentReady();

    tryReleaseKlaviyo();

    if (klaviyoReleased || (tooLongOrbe && tooLongConsent)) {
      clearInterval(poll);
      if (!klaviyoReleased) {
        revealCookiesAfterOrbe();
        markConsentReady();
        tryReleaseKlaviyo();
        if (!klaviyoReleased) releaseKlaviyo();
      }
    }
  }, POLL_MS);

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {
      syncHoldFromApi();
      watchBanner();
      watchOrbe();
    });
  }
})();
